import { useTranslation } from 'react-i18next';

/**
 * Custom hook for RTL/LTR support
 * Provides utilities for components to adapt to different text directions
 */
export const useRTL = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return {
    isRTL,
    direction: isRTL ? 'rtl' : 'ltr',
    textAlign: isRTL ? 'right' : 'left',
    flexDirection: isRTL ? 'row-reverse' : 'row',
    marginStart: isRTL ? 'marginRight' : 'marginLeft',
    marginEnd: isRTL ? 'marginLeft' : 'marginRight',
    paddingStart: isRTL ? 'paddingRight' : 'paddingLeft',
    paddingEnd: isRTL ? 'paddingLeft' : 'paddingRight',
    borderStart: isRTL ? 'borderRight' : 'borderLeft',
    borderEnd: isRTL ? 'borderLeft' : 'borderRight',
    positionStart: isRTL ? 'right' : 'left',
    positionEnd: isRTL ? 'left' : 'right',
    
    // Helper function to get appropriate styles for MUI components
    getListItemStyles: () => ({
      flexDirection: isRTL ? 'row-reverse' : 'row',
      justifyContent: isRTL ? 'flex-end' : 'flex-start',
      textAlign: isRTL ? 'right' : 'left',
      gap: { xs: 1, md: 1.5 },
    }),
    
    // Helper function for button styles
    getButtonStyles: () => ({
      flexDirection: isRTL ? 'row-reverse' : 'row',
      textAlign: isRTL ? 'right' : 'left',
    }),
    
    // Helper function for form styles
    getFormStyles: () => ({
      textAlign: isRTL ? 'right' : 'left',
      direction: isRTL ? 'rtl' : 'ltr',
    }),
    
    // Helper function for table styles
    getTableStyles: () => ({
      direction: isRTL ? 'rtl' : 'ltr',
      textAlign: isRTL ? 'right' : 'left',
    }),
    
    // Helper function for icon positioning with proper spacing
    getIconPosition: (icon, text) => {
      return isRTL ? [text, icon] : [icon, text];
    },
    
    // Helper function for margin/padding
    getSpacing: (start, end = start) => {
      return isRTL 
        ? { marginRight: start, marginLeft: end }
        : { marginLeft: start, marginRight: end };
    },
    
    // Helper function for padding
    getPadding: (start, end = start) => {
      return isRTL 
        ? { paddingRight: start, paddingLeft: end }
        : { paddingLeft: start, paddingRight: end };
    },
    
    // Helper function for TextField RTL support
    getTextFieldProps: (type = 'text') => {
      // For date and time inputs, use standard LTR styling to prevent label overlap
      if (type === 'date' || type === 'time') {
        return {
          inputProps: {
            dir: 'ltr',
            style: { 
              textAlign: 'left'
            }
          },
          InputLabelProps: {
            shrink: true,
            sx: {
              left: 14,
              right: 'auto',
              transformOrigin: 'top left',
              '&.MuiInputLabel-shrink': {
                left: 0,
                transform: 'translate(14px, -1.5px) scale(0.75)',
              }
            }
          },
          sx: {
            '& .MuiInputLabel-root': {
              transformOrigin: 'top left',
              left: 14,
              right: 'auto',
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(14px, -1.5px) scale(0.75)',
              transformOrigin: 'top left',
              left: 0,
              right: 'auto',
            },
            '& .MuiOutlinedInput-notchedOutline legend': {
              textAlign: 'left',
            }
          }
        };
      }

      // Standard RTL/LTR handling for other input types
      return {
        inputProps: {
          dir: isRTL ? 'rtl' : 'ltr',
          style: { 
            textAlign: isRTL ? 'right' : 'left'
          }
        },
        InputLabelProps: isRTL ? {
          sx: {
            right: 24,
            left: 'auto',
            transformOrigin: 'top right',
            '&.MuiInputLabel-shrink': {
              right: 24,
              transform: 'translate(0, -1.5px) scale(0.75)',
            }
          }
        } : {},
        sx: {
          '& .MuiInputLabel-root': {
            transformOrigin: isRTL ? 'top right' : 'top left',
            right: isRTL ? 24 : 'auto',
            left: isRTL ? 'auto' : 14,
          },
          '& .MuiInputLabel-shrink': {
            transform: isRTL 
              ? 'translate(0, -1.5px) scale(0.75)' 
              : 'translate(14px, -1.5px) scale(0.75)',
            transformOrigin: isRTL ? 'top right' : 'top left',
            right: isRTL ? 24 : 'auto',
            left: isRTL ? 'auto' : 0,
          },
          '& .MuiOutlinedInput-notchedOutline legend': {
            textAlign: isRTL ? 'right' : 'left',
          }
        }
      };
    },
    
    // Helper function for FormControl RTL support
    getFormControlProps: () => ({
      sx: {
        '& .MuiInputLabel-root': {
          transformOrigin: isRTL ? 'top right' : 'top left',
          right: isRTL ? 24 : 'auto',
          left: isRTL ? 'auto' : 0,
        },
        '& .MuiSelect-select': {
          textAlign: isRTL ? 'right' : 'left',
          direction: isRTL ? 'rtl' : 'ltr',
          paddingRight: isRTL ? '14px' : '32px',
          paddingLeft: isRTL ? '32px' : '14px'
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
          textAlign: isRTL ? 'right' : 'left',
        },
        '& .MuiSelect-icon': {
          right: isRTL ? 'auto' : '7px',
          left: isRTL ? '7px' : 'auto'
        }
      }
    })
  };
};

export default useRTL;
